import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { DisplayType } from '../../redux/notifications/types';
import CloseIcon from '../Generic/Buttons/StyledCloseButton';
import { textStyle } from '../GlobalStyles/theme/text';
import { Color } from '../GlobalStyles/theme';
import { BellaTestIds } from '../../../common/qaIds';

export const transitionTimeSeconds = 0.3;

const NotificationBlock = styled.div<{ isVisible: boolean; background: Color; color: Color }>`
    background: ${({ theme, background }) => theme.colors[background || 'charcoal100']};
    color: ${({ theme, color }) => theme.colors[color || 'charcoal100']};
    padding-right: 10px;
    padding-left: 10px;
    display: grid;
    grid-template-columns: 20px [content]1fr [dismiss]20px;
    justify-items: center;
    align-items: center;
    transition: all ${() => `${transitionTimeSeconds}`}s ease-out;

    overflow: hidden;
    opacity: 0;
    max-height: 0;

    ${({ isVisible }) =>
        isVisible &&
        css`
            padding-top: 10px;
            padding-bottom: 10px;
            opacity: 1;
            max-height: unset;
        `}
`;

const NotificationMessageText = styled.span`
    ${textStyle('body2')}

    grid-column: content;
`;

const NotificationDismissButton = styled.button`
    grid-column: dismiss;
`;
const NotificationDismissIcon = styled(CloseIcon)`
    margin: 0;
`;

interface NotificationBannerProps {
    message: string;
    messageId: string;
    autoHideIntervalSeconds?: number | 'sticky';
    displayType?: DisplayType;
    onMessageDismissed: (messageId: string) => void;
}

enum NotificationState {
    showing = 'showing',
    shown = 'shown',
    hiding = 'hiding',
}
interface NotificationStyleColors {
    background: Color;
    color: Color;
}

const getNotificationStyleColors = (displayType?: DisplayType): NotificationStyleColors => {
    switch (displayType) {
        case DisplayType.warning: {
            return { background: 'yellow30', color: 'charcoal100' };
        }
        case DisplayType.error: {
            return { background: 'red10', color: 'red100' };
        }
        case DisplayType.hint: {
            return { background: 'yellow90', color: 'charcoal80' };
        }
        default: {
            return { background: 'turquoise60', color: 'white' };
        }
    }
};

const NotificationBanner: React.FC<NotificationBannerProps> = ({
    message,
    messageId,
    onMessageDismissed,
    autoHideIntervalSeconds,
    displayType,
}) => {
    const [notificationState, setNotificationState] = useState(NotificationState.showing);
    const isVisible = notificationState === NotificationState.shown;
    useEffect(() => {
        if (notificationState === NotificationState.showing) {
            const timer = setTimeout(() => setNotificationState(NotificationState.shown), 0);
            return () => clearTimeout(timer);
        }

        if (
            notificationState === NotificationState.shown &&
            autoHideIntervalSeconds !== 'sticky' &&
            autoHideIntervalSeconds
        ) {
            const timer = setTimeout(
                () => setNotificationState(NotificationState.hiding),
                autoHideIntervalSeconds * 1000
            );
            return () => clearTimeout(timer);
        }

        if (notificationState === NotificationState.hiding) {
            const timer = setTimeout(() => onMessageDismissed(messageId), transitionTimeSeconds * 1000);
            return () => clearTimeout(timer);
        }

        return () => {};
    }, [notificationState, messageId, autoHideIntervalSeconds, onMessageDismissed]);

    const { color, background } = getNotificationStyleColors(displayType);

    return (
        <NotificationBlock
            data-testid={BellaTestIds.notificationBanner}
            isVisible={isVisible}
            color={color}
            background={background}
        >
            <NotificationMessageText>{message}</NotificationMessageText>
            <NotificationDismissButton type="button">
                <NotificationDismissIcon
                    data-testid="dismiss-notification-button"
                    color={color}
                    onClick={() => setNotificationState(NotificationState.hiding)}
                />
            </NotificationDismissButton>
        </NotificationBlock>
    );
};

export default NotificationBanner;
